import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Contact as ContactIcon, Plus, Search, Upload, Trash2, Mail, Phone, ShieldOff, ShieldCheck, Loader2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/app/PageHeader";
import { Loader, EmptyState } from "@/components/app/Loader";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

export default function Contacts() {
  const { hasRole } = useAuth();
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [q, setQ] = useState("");
  const [tag, setTag] = useState("all");

  const params = {};
  if (q) params.q = q;
  if (tag !== "all") params.tag = tag;

  const { data, isLoading } = useQuery({ queryKey: ["contacts", params], queryFn: () => api.get("/contacts", { params }).then((r) => r.data) });
  const { data: tags } = useQuery({ queryKey: ["contactTags"], queryFn: () => api.get("/contacts/tags").then((r) => r.data) });

  const patch = useMutation({
    mutationFn: ({ id, body }) => api.patch(`/contacts/${id}`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["contacts"] }); qc.invalidateQueries({ queryKey: ["overview"] }); },
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/contacts/${id}`),
    onSuccess: () => { toast.success("Contact removed"); qc.invalidateQueries({ queryKey: ["contacts"] }); },
  });

  const doImport = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/contacts/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.imported} · skipped ${data.skipped}`);
      qc.invalidateQueries({ queryKey: ["contacts"] });
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader
        title="Contacts"
        subtitle="Opt-in volunteers and supporters: the only universe that can be emailed. Consent is tracked; voters never merge in here."
        icon={ContactIcon}
        actions={
          hasRole("admin", "manager", "staff") && (
            <div className="flex gap-2">
              <input ref={fileRef} type="file" accept=".csv,.xlsx" className="hidden" onChange={(e) => doImport(e.target.files?.[0])} />
              <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="import-contacts-btn"><Upload className="mr-2 h-4 w-4" /> Import CSV</Button>
              <ContactDialog />
            </div>
          )
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-border bg-white p-3 shadow-sm">
        <div className="relative min-w-[200px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-mute" />
          <Input data-testid="contacts-search" placeholder="Search contacts…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
        </div>
        <Select value={tag} onValueChange={setTag}>
          <SelectTrigger className="w-[160px]" data-testid="tag-filter"><SelectValue placeholder="Tag" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All tags</SelectItem>
            {tags?.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <Loader />
      ) : !data?.length ? (
        <EmptyState icon={ContactIcon} title="No contacts" description="Add opt-in volunteers and supporters to build a sendable list." />
      ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-white shadow-sm">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow><TableHead>Name</TableHead><TableHead>Contact</TableHead><TableHead>Tags</TableHead><TableHead>Email consent</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {data.map((c) => (
                  <TableRow key={c.id} data-testid="contact-row">
                    <TableCell className="font-medium text-brand-ink">{c.firstName} {c.lastName}{c.voter_ref && <Badge variant="outline" className="ml-2 text-[0.6rem]">linked voter</Badge>}</TableCell>
                    <TableCell>
                      <div className="space-y-0.5 text-xs text-brand-mute">
                        {c.email && <p className="flex items-center gap-1"><Mail className="h-3 w-3" /> {c.email}</p>}
                        {c.phone && <p className="flex items-center gap-1"><Phone className="h-3 w-3" /> {c.phone}</p>}
                      </div>
                    </TableCell>
                    <TableCell><div className="flex flex-wrap gap-1">{c.tags?.map((t) => <Badge key={t} variant="outline" className="text-[0.65rem]">{t}</Badge>)}</div></TableCell>
                    <TableCell>
                      <Switch
                        checked={!!c.consent_email && !c.opt_out}
                        disabled={c.opt_out || !hasRole("admin", "manager", "staff")}
                        onCheckedChange={(v) => patch.mutate({ id: c.id, body: { consent_email: v } })}
                        data-testid={`consent-toggle-${c.id}`}
                      />
                    </TableCell>
                    <TableCell>
                      {c.opt_out ? (
                        <Badge className="bg-neg-bg text-neg-text"><ShieldOff className="mr-1 h-3 w-3" /> Opted out</Badge>
                      ) : (
                        <Badge className="bg-pos-bg text-pos-text"><ShieldCheck className="mr-1 h-3 w-3" /> Active</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {hasRole("admin", "manager", "staff") && (
                        <Button variant="ghost" size="icon" onClick={() => del.mutate(c.id)} data-testid={`delete-contact-${c.id}`}><Trash2 className="h-4 w-4 text-brand-red" /></Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}

function ContactDialog() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", phone: "", tags: "", consent_email: true });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!form.firstName.trim()) return toast.error("First name is required");
    setBusy(true);
    try {
      await api.post("/contacts", {
        firstName: form.firstName, lastName: form.lastName,
        email: form.email || undefined, phone: form.phone,
        tags: form.tags ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : [],
        consent_email: form.consent_email,
      });
      toast.success("Contact added");
      qc.invalidateQueries({ queryKey: ["contacts"] });
      setOpen(false);
      setForm({ firstName: "", lastName: "", email: "", phone: "", tags: "", consent_email: true });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-brand-red text-white hover:bg-brand-redhover" data-testid="new-contact-btn"><Plus className="mr-2 h-4 w-4" /> Add contact</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-heading">New opt-in contact</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>First name</Label><Input value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} className="mt-1.5" data-testid="contact-first-input" /></div>
          <div><Label>Last name</Label><Input value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} className="mt-1.5" /></div>
          <div className="col-span-2"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" data-testid="contact-email-input" /></div>
          <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Tags (comma)</Label><Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="volunteer, donor" className="mt-1.5" /></div>
        </div>
        <div className="flex items-center gap-2"><Switch checked={form.consent_email} onCheckedChange={(v) => setForm({ ...form, consent_email: v })} /><Label className="cursor-pointer">Has opted in to email</Label></div>
        <DialogFooter><Button onClick={save} disabled={busy} className="bg-brand-red text-white hover:bg-brand-redhover" data-testid="save-contact-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add contact"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
